import React, { useEffect, useRef, useState, useCallback } from "react";
import { gsap, Back } from "gsap";
import Delaunator from "delaunator";
import "./ImageShatter.css";
/* eslint-disable react-hooks/exhaustive-deps */

const TWO_PI = Math.PI * 2;
const AUTO_DELAY = 5000; // miliseconds

const safeRemove = (parent, child) => {
  if (parent && child && parent.contains(child)) {
    parent.removeChild(child);
  }
};

function ImageShatter({ images = [], height = 485 }) {
  const containerRef = useRef(null);
  const imagesRef = useRef([]);
  const imageRef = useRef(null);
  const imageIndexRef = useRef(0);

  const clickPosition = useRef([0, 0]);
  const vertices = useRef([]);
  const indices = useRef([]);
  const fragments = useRef([]);

  const autoTimerRef = useRef(null);
  const isPausedRef = useRef(false);

  const [activeIndex, setActiveIndex] = useState(0);

  /* ================= TIMER ================= */
  const stopAutoTimer = useCallback(() => {
    clearInterval(autoTimerRef.current);
  }, []);

  const startAutoTimer = useCallback(() => {
    stopAutoTimer();
    autoTimerRef.current = setInterval(() => {
      if (!isPausedRef.current) autoShatter();
    }, AUTO_DELAY);
  }, [stopAutoTimer]);

  const resetAutoTimer = useCallback(() => {
    stopAutoTimer();
    startAutoTimer();
  }, [stopAutoTimer, startAutoTimer]);

  /* ================= IMAGE FLOW ================= */
  const placeImage = useCallback(
    (transition) => {
      const container = containerRef.current;
      const imgs = imagesRef.current;
      if (!container || !imgs.length) return;

      const img = imgs[imageIndexRef.current];
      imageRef.current = img;
      setActiveIndex(imageIndexRef.current);

      imageIndexRef.current =
        (imageIndexRef.current + 1) % imgs.length;

      img.style.position = "absolute";
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.left = 0;
      img.style.top = 0;
      img.style.cursor = "pointer";
      img.onclick = imageClickHandler;

      container.querySelectorAll("img").forEach((i) =>
        safeRemove(container, i)
      );
      container.appendChild(img);

      if (transition) {
        gsap.fromTo(
          img,
          { y: -1000 },
          { y: 0, duration: 0.75, ease: Back.easeOut }
        );
      }
    },
    []
  );

  const init = useCallback(() => {
    placeImage(false);
    startAutoTimer();
  }, [placeImage, startAutoTimer]);

  /* ================= IMAGE LOADING ================= */
  useEffect(() => {
    if (!images.length) return;

    let loaded = 0;
    const imgs = images.map((src) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        loaded++;
        if (loaded === images.length) {
          imagesRef.current = imgs;
          init();
        }
      };
      img.src = src;
      return img;
    });
  }, [images, init]);

  /* ================= RESIZE ================= */
  useEffect(() => {
    const onResize = () => {
      stopAutoTimer();
      cleanupFragments();
      placeImage(false);
      startAutoTimer();
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [placeImage, startAutoTimer, stopAutoTimer]);

  /* ================= HOVER PAUSE ================= */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const pause = () => {
      isPausedRef.current = true;
      stopAutoTimer();
    };

    const resume = () => {
      isPausedRef.current = false;
      startAutoTimer();
    };

    el.addEventListener("mouseenter", pause);
    el.addEventListener("mouseleave", resume);

    return () => {
      el.removeEventListener("mouseenter", pause);
      el.removeEventListener("mouseleave", resume);
    };
  }, [startAutoTimer, stopAutoTimer]);

  /* ================= CLICK / AUTO ================= */
  function imageClickHandler(e) {
    resetAutoTimer();
    const img = imageRef.current;
    if (!img) return;

    const box = img.getBoundingClientRect();
    clickPosition.current = [
      ((e.clientX - box.left) * img.naturalWidth) / img.clientWidth,
      ((e.clientY - box.top) * img.naturalHeight) / img.clientHeight
    ];

    triangulate();
    shatter();
  }

  function autoShatter() {
    const img = imageRef.current;
    if (!img) return;

    clickPosition.current = [
      Math.random() * img.naturalWidth,
      Math.random() * img.naturalHeight
    ];

    triangulate();
    shatter();
  }

  /* ================= TRIANGULATION ================= */
  function triangulate() {
    const img = imageRef.current;
    if (!img) return;

    vertices.current.length = 0;
    vertices.current.push(clickPosition.current);

    const rings = [
      { r: 50, c: 12 },
      { r: 150, c: 12 },
      { r: 300, c: 12 },
      { r: 1200, c: 12 }
    ];

    rings.forEach((r) => {
      for (let i = 0; i < r.c; i++) {
        vertices.current.push([
          clamp(
            Math.cos((i / r.c) * TWO_PI) * r.r +
              clickPosition.current[0],
            0,
            img.naturalWidth
          ),
          clamp(
            Math.sin((i / r.c) * TWO_PI) * r.r +
              clickPosition.current[1],
            0,
            img.naturalHeight
          )
        ]);
      }
    });

    indices.current = Delaunator.from(vertices.current).triangles;
  }

  /* ================= SHATTER ================= */
  function shatter() {
    const container = containerRef.current;
    const img = imageRef.current;
    if (!container || !img) return;

    const sx = img.clientWidth / img.naturalWidth;
    const sy = img.clientHeight / img.naturalHeight;

    const tl = gsap.timeline({ onComplete: shatterComplete });

    for (let i = 0; i < indices.current.length; i += 3) {
      const f = new Fragment(
        vertices.current[indices.current[i]],
        vertices.current[indices.current[i + 1]],
        vertices.current[indices.current[i + 2]],
        img,
        sx,
        sy
      );

      const dx = f.centroid[0] * sx - clickPosition.current[0] * sx;
      const dy = f.centroid[1] * sy - clickPosition.current[1] * sy;
      const d = Math.sqrt(dx * dx + dy * dy);

      const tl1 = gsap.timeline();
      tl1.to(f.canvas, {
        duration: 1,
        z: -500,
        rotationX: 30 * Math.sign(dy),
        rotationY: -90 * Math.sign(dx),
        ease: "power1.in"
      });
      tl1.to(f.canvas, { alpha: 0, duration: 0.4, delay: 0.6 });

      tl.add(tl1, d * 0.003);
      fragments.current.push(f);
      container.appendChild(f.canvas);
    }

    safeRemove(container, img);
  }

  function shatterComplete() {
    cleanupFragments();
    placeImage(true);
  }

  function cleanupFragments() {
    const container = containerRef.current;
    fragments.current.forEach((f) => {
      if (f.canvas.parentNode === container) {
        safeRemove(container, f.canvas);
      }
    });
    fragments.current.length = 0;
  }

  /* ================= FRAGMENT ================= */
  function Fragment(v0, v1, v2, img, sx, sy) {
    this.v0 = v0;
    this.v1 = v1;
    this.v2 = v2;

    this.box = {
      x: Math.min(v0[0], v1[0], v2[0]),
      y: Math.min(v0[1], v1[1], v2[1]),
      w:
        Math.max(v0[0], v1[0], v2[0]) -
        Math.min(v0[0], v1[0], v2[0]),
      h:
        Math.max(v0[1], v1[1], v2[1]) -
        Math.min(v0[1], v1[1], v2[1])
    };

    this.centroid = [
      (v0[0] + v1[0] + v2[0]) / 3,
      (v0[1] + v1[1] + v2[1]) / 3
    ];

    this.canvas = document.createElement("canvas");
    this.canvas.width = this.box.w * sx;
    this.canvas.height = this.box.h * sy;
    this.canvas.style.position = "absolute";
    this.canvas.style.left = this.box.x * sx + "px";
    this.canvas.style.top = this.box.y * sy + "px";

    const ctx = this.canvas.getContext("2d");
    ctx.scale(sx, sy);
    ctx.translate(-this.box.x, -this.box.y);
    ctx.beginPath();
    ctx.moveTo(v0[0], v0[1]);
    ctx.lineTo(v1[0], v1[1]);
    ctx.lineTo(v2[0], v2[1]);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(img, 0, 0);
  }

  const clamp = (v, a, b) => Math.max(a, Math.min(v, b));

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height, position: "relative", overflow: "hidden" }}
    >
      <div className="shatter-dots">
        {images.map((_, i) => (
          <span
            key={i}
            className={`dot ${i === activeIndex ? "active" : ""}`}
            onClick={() => {
              resetAutoTimer();
              imageIndexRef.current = i;
              autoShatter();
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default ImageShatter;
